/**
 * Anlagen-Seite (Generation Units)
 * Zeigt die Projektkarte und darunter alle Anlagen des Projekts in einer Tabelle
 */
import React, { useCallback, useMemo, useState } from "react";
import { ProjectDetailPageProps } from "../../../types";
import { useGenerationUnitsData } from "./hooks/useGenerationUnitsData";
import {
  MapSection,
  useProjectGeometrie,
  useStandortSpeichern,
} from "../../map";
import PageTemplate from "../../layout/PageTemplate";
import StatChip from "../../common/display/StatChip";
import GenerationUnitsSection from "./sections/GenerationUnitsSection";
import { formatPowerMW } from "../../../utils/formatters";
import { colors } from "../../../styles/theme";

interface GenerationUnitsPageProps extends ProjectDetailPageProps {
  canView: boolean;
  canEdit: boolean;
}

const GenerationUnitsPage: React.FC<GenerationUnitsPageProps> = ({
  project,
  canEdit,
}) => {
  const {
    units,
    loading,
    error,
    reload: reloadUnits,
    createUnits,
    updateUnit,
    deleteUnit,
  } = useGenerationUnitsData({ projectId: project?.id });
  const {
    geometrie,
    areaId,
    standortKoordinate,
    reload: reloadGeometrie,
  } = useProjectGeometrie(project?.id);
  const [selectedUnitId, setSelectedUnitId] = useState<string | null>(null);
  const handleKoordinateChange = useStandortSpeichern(project.id);

  /** Nach Flächen-Upload: Geometrie + Anlagen neu laden (Backend setzt Koordinaten) */
  const handleGeometrieChanged = useCallback(async () => {
    await reloadGeometrie();
    await reloadUnits();
  }, [reloadGeometrie, reloadUnits]);

  /** Anlage per Drag&Drop auf der Karte verschoben → Koordinaten im Backend speichern */
  const handleAnlagePositionChange = useCallback(
    async (unitId: string, breitengrad: number, laengengrad: number) => {
      await updateUnit(unitId, { breitengrad, laengengrad });
    },
    [updateUnit],
  );

  const gesamtleistungMW = useMemo(
    () => units.reduce((sum, u) => sum + (u.nennleistung ?? 0), 0),
    [units],
  );

  const mapTitleSuffix = (
    <>
      <StatChip
        value={`${units.length}`}
        label="Anlagen"
        backgroundColor={colors.brand.teal50}
        valueColor={colors.brand.teal600}
      />
      <StatChip
        value={`${formatPowerMW(gesamtleistungMW)} MW`}
        label="Nennleistung"
        backgroundColor={colors.brand.teal50}
        valueColor={colors.brand.teal600}
      />
    </>
  );

  return (
    <PageTemplate isLoading={loading} error={error}>
      <MapSection
        titleSuffix={mapTitleSuffix}
        projectId={project.id}
        projectName={project.name}
        geometrie={geometrie}
        areaId={areaId}
        anlagen={units}
        onGeometrieChanged={handleGeometrieChanged}
        onAnlagePositionChange={handleAnlagePositionChange}
        onAnlageSelect={setSelectedUnitId}
        selectedAnlageId={selectedUnitId ?? undefined}
        standortKoordinate={standortKoordinate}
        onStandortSaved={handleKoordinateChange}
      />

      <GenerationUnitsSection
        projectId={project.id}
        units={units}
        canEdit={canEdit}
        onCreate={async (items) => {
          await createUnits(items);
        }}
        onUpdate={async (unitId, data) => {
          await updateUnit(unitId, data);
        }}
        onDelete={deleteUnit}
        selectedUnitId={selectedUnitId ?? undefined}
      />
    </PageTemplate>
  );
};

export default GenerationUnitsPage;
